/**
 * Quick Comments — horizontal scroll of template comment chips.
 * Context-aware: different templates per post type.
 */

import { memo, useCallback } from 'react'
import { useQuickComments } from '@turnifly/shared/hooks'
import type { QuickComment } from '@turnifly/shared/api'

const TYPE_TO_CONTEXT: Record<string, string> = {
  streak_milestone: 'achievement',
  challenge_completed: 'achievement',
  goal_achieved: 'achievement',
  achievement_earned: 'achievement',
  challenge_joined: 'support',
  goal_announced: 'support',
  user_post: 'general',
  proof: 'general',
  repost: 'general',
}

interface QuickCommentsProps {
  postType: string
  onSend: (text: string) => void
}

export const QuickComments = memo(function QuickComments({
  postType,
  onSend,
}: QuickCommentsProps): React.ReactElement | null {
  const { data: allComments } = useQuickComments()

  const context = TYPE_TO_CONTEXT[postType] ?? 'general'
  const filtered = (allComments ?? []).filter((c) => c.context === context)

  const handleClick = useCallback((comment: QuickComment): void => {
    onSend(comment.text_uz)
  }, [onSend])

  if (filtered.length === 0) return null

  return (
    <div className="flex gap-1.5 overflow-x-auto scrollbar-hide py-2 -mx-1 px-1">
      {filtered.map((c) => (
        <button
          key={c.id}
          onClick={() => handleClick(c)}
          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.05] text-[11px] text-text-hint/70 hover:bg-white/[0.06] hover:text-text transition-colors tap-highlight-transparent"
        >
          {c.icon && <span className="text-[12px]">{c.icon}</span>}
          <span>{c.text_uz}</span>
        </button>
      ))}
    </div>
  )
})
